import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ChevronLeft, ChevronRight, Trash2, Save, Sparkles,
  CircleDashed, CircleCheck, SlidersVertical, ClockCheck, Check,
  Scan, ShieldCheck, CircleHelp, Bell, CloudDownload, Printer
} from 'lucide-react'
import Avatar from '../../../components/Avatar/Avatar'
import ConfirmModal from '../../../components/ERP/ConfirmModal/ConfirmModal'
import ERPSelectSearchModal, { ERPSelectModalItem, ERPSelectModalEmpty } from '../../../components/ERP/Modal/ERPSelectSearchModal'
import OnboardingTour from '../../../components/ERP/Onboarding/OnboardingTour'
import Toast from '../../../components/Toast/Toast'
import { dutyRosterService } from '../../../services/dutyRosterApi'
import { useAuth } from '../../../contexts/AuthContext'
import { downloadDutySheet, printDutySheet, rosterFileName } from './dutyRosterPrint'
import { usePermissions, MODULES, ACTIONS } from '../../../hooks/usePermissions'
import { useToast } from '../../../hooks/useToast'
import { DUTY_ROSTER_ONBOARDING_STEPS, DUTY_ROSTER_TOUR_ID } from './dutyRosterOnboarding'
import '../Financeiro/Dashboard/FinanceDashboardPage.css'
import '../Pacientes/PatientsPage.css'
import './DutyRosterPage.css'

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
const STARTS = ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00']
const SCALE_RULES = [
  { id: '12x36', label: '12x36', detail: '12 horas e 36 de folga' },
  { id: '24x72', label: '24x72', detail: '24 horas e 72 de folga' },
  { id: 'diurno', label: 'Diurno', detail: 'Só o turno do dia' },
  { id: 'rotativo', label: 'Rotativo', detail: 'Dia e noite, sem folga extra' }
]

function currentMonth() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit'
  }).formatToParts(new Date())
  return {
    year: Number(parts.find((part) => part.type === 'year').value),
    month: Number(parts.find((part) => part.type === 'month').value)
  }
}

function dateKey(year, month, day) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function monthGrid(year, month) {
  const first = new Date(year, month - 1, 1)
  const count = new Date(year, month, 0).getDate()
  const cells = Array.from({ length: first.getDay() }, () => null)
  for (let day = 1; day <= count; day += 1) cells.push(day)
  while (cells.length % 7) cells.push(null)
  return cells
}

function ranges(dayStart) {
  const [hour, minute] = String(dayStart).split(':').map(Number)
  const end = `${String((hour + 12) % 24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
  return { day: `${dayStart}–${end}`, night: `${end}–${dayStart}` }
}

function firstName(name) {
  return String(name || '').trim().split(/\s+/)[0] || ''
}

function messageFrom(error, fallback) {
  return error?.response?.data?.message || fallback
}

export default function DutyRosterPage() {
  const { user, organizations, currentSchema } = useAuth()
  const { hasPermission } = usePermissions()
  const { toast, showToast, hideToast } = useToast()
  const canEdit = hasPermission(MODULES.DUTY_ROSTERS, ACTIONS.DUTY_ROSTERS_EDIT)
  const canGenerate = hasPermission(MODULES.DUTY_ROSTERS, ACTIONS.DUTY_ROSTERS_GENERATE)
  const canSave = hasPermission(MODULES.DUTY_ROSTERS, ACTIONS.DUTY_ROSTERS_SAVE)
  const initial = currentMonth()
  const [tab, setTab] = useState('calendar')
  const [patients, setPatients] = useState([])
  const [staff, setStaff] = useState([])
  const [groups, setGroups] = useState([])
  const [restricted, setRestricted] = useState({})
  const [bypassUserIds, setBypassUserIds] = useState([])
  const [accessOnly, setAccessOnly] = useState(true)
  const [groupId, setGroupId] = useState(null)
  const [peopleQuery, setPeopleQuery] = useState('')
  const [patientId, setPatientId] = useState('')
  const [marked, setMarked] = useState([])
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [year, setYear] = useState(initial.year)
  const [month, setMonth] = useState(initial.month)
  const [shifts, setShifts] = useState([])
  const [rosters, setRosters] = useState([])
  const [busy, setBusy] = useState(false)
  const [confirm, setConfirm] = useState(null)
  const [ready, setReady] = useState(false)
  const [tourReplay, setTourReplay] = useState(0)
  const [openDay, setOpenDay] = useState(null)
  const [rule, setRule] = useState('12x36')
  const [dayStart, setDayStart] = useState('07:00')
  const [scaleOpen, setScaleOpen] = useState(false)
  const [clockOpen, setClockOpen] = useState(false)
  const [picker, setPicker] = useState(null)
  const [pickerQuery, setPickerQuery] = useState('')

  const patient = patients.find((item) => String(item.id) === String(patientId))
  const activeRule = SCALE_RULES.find((item) => item.id === rule) || SCALE_RULES[0]
  const hours = ranges(dayStart)
  const grid = monthGrid(year, month)
  const activeGroup = groups.find((item) => Number(item.id) === Number(groupId))
  const allowedIds = restricted[String(patientId)]

  const personAllowed = (person) => {
    if (!accessOnly) return true
    if (!allowedIds) return true
    if (person.role === 'admin' || bypassUserIds.map(Number).includes(Number(person.id))) return true
    return allowedIds.map(Number).includes(Number(person.id))
  }

  const filteredStaff = staff.filter((item) => {
    if (!personAllowed(item)) return false
    if (groupId && Number(item.access_group_id) !== Number(groupId)) return false
    const query = peopleQuery.trim().toLowerCase()
    if (!query) return true
    return `${item.name} ${item.access_group_name || ''}`.toLowerCase().includes(query)
  })

  const pickerItems = useMemo(() => {
    const query = pickerQuery.trim().toLowerCase()
    let source = picker?.kind === 'patient' ? patients : staff
    if (picker?.kind === 'person') {
      source = source.filter((item) => {
        if (groupId && Number(item.access_group_id) !== Number(groupId)) return false
        if (!accessOnly || !allowedIds) return true
        if (item.role === 'admin' || bypassUserIds.map(Number).includes(Number(item.id))) return true
        return allowedIds.map(Number).includes(Number(item.id))
      })
    }
    return source.filter((item) => {
      if (!query) return true
      return `${item.name} ${item.bed || ''} ${item.recordNumber || ''} ${item.access_group_name || ''}`.toLowerCase().includes(query)
    })
  }, [picker, pickerQuery, patients, staff, groupId, accessOnly, allowedIds, bypassUserIds])

  const loadShifts = useCallback(async () => {
    if (!patientId) {
      setShifts([])
      return
    }
    const response = await dutyRosterService.shifts({ patientId, year, month })
    setShifts(response.data?.data || [])
  }, [patientId, year, month])

  const loadRosters = useCallback(async () => {
    const response = await dutyRosterService.list()
    setRosters(response.data?.data || [])
  }, [])

  useEffect(() => {
    let active = true
    dutyRosterService.options()
      .then((response) => {
        if (!active) return
        const list = response.data?.data?.patients || []
        setPatients(list)
        setStaff(response.data?.data?.staff || [])
        setGroups(response.data?.data?.groups || [])
        setRestricted(response.data?.data?.restricted || {})
        setBypassUserIds(response.data?.data?.bypassUserIds || [])
        setPatientId((current) => current || (list[0] ? String(list[0].id) : ''))
        setReady(true)
      })
      .catch((err) => {
        if (active) showToast(messageFrom(err, 'Não foi possível carregar a escala'), 'error')
      })
    loadRosters().catch(() => {})
    return () => { active = false }
  }, [loadRosters, showToast])

  useEffect(() => {
    loadShifts().catch((err) => showToast(messageFrom(err, 'Não foi possível carregar os plantões'), 'error'))
  }, [loadShifts, showToast])

  const shiftOn = (day, slot) => shifts.find((item) => item.shift_date === dateKey(year, month, day) && item.slot === slot)

  const toggleMarked = (id) => {
    setMarked((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]))
  }

  const visibleIds = filteredStaff.map((person) => Number(person.id))
  const allVisibleMarked = visibleIds.length > 0 && visibleIds.every((id) => marked.includes(id))

  const toggleAllMarked = () => {
    setMarked((current) => (
      allVisibleMarked
        ? current.filter((id) => !visibleIds.includes(id))
        : [...new Set([...current, ...visibleIds])]
    ))
  }

  const changeMonth = (delta) => {
    const cursor = new Date(year, month - 1 + delta, 1)
    setYear(cursor.getFullYear())
    setMonth(cursor.getMonth() + 1)
    setOpenDay(null)
  }

  const place = async (day, slot, userId = selectedUserId) => {
    if (!canEdit || !patientId || !userId) return
    setBusy(true)
    try {
      await dutyRosterService.place({
        patientId: Number(patientId),
        userId,
        date: dateKey(year, month, day),
        slot,
        dayStart,
        rule
      })
      setSelectedUserId(null)
      showToast('Plantão marcado.', 'success')
      await loadShifts()
    } catch (err) {
      showToast(messageFrom(err, 'Não foi possível marcar o plantão'), 'error')
    } finally {
      setBusy(false)
    }
  }

  const onDrop = (event, day, slot) => {
    event.preventDefault()
    const userId = Number(event.dataTransfer.getData('text/plain'))
    if (userId) place(day, slot, userId)
  }

  const changeShiftUser = async (shiftId, userId) => {
    setBusy(true)
    try {
      await dutyRosterService.update(shiftId, { userId })
      setPicker(null)
      showToast('Pessoa trocada.', 'success')
      await loadShifts()
    } catch (err) {
      showToast(messageFrom(err, 'Não foi possível trocar a pessoa'), 'error')
    } finally {
      setBusy(false)
    }
  }

  const removeShift = async (shiftId) => {
    setBusy(true)
    try {
      await dutyRosterService.remove(shiftId)
      showToast('Plantão tirado.', 'success')
      await loadShifts()
    } catch (err) {
      showToast(messageFrom(err, 'Não foi possível tirar o plantão'), 'error')
    } finally {
      setBusy(false)
    }
  }

  const clearMonth = async () => {
    if (!patientId) return
    setBusy(true)
    try {
      await dutyRosterService.clear({ patientId, year, month })
      showToast('Mês limpo.', 'success')
      await loadShifts()
    } catch (err) {
      showToast(messageFrom(err, 'Não foi possível limpar o mês'), 'error')
    } finally {
      setBusy(false)
    }
  }

  const generate = async () => {
    if (!patientId) return
    setBusy(true)
    try {
      const response = await dutyRosterService.generate({
        patientId: Number(patientId),
        year,
        month,
        userIds: marked,
        dayStart,
        rule
      })
      const payload = response.data?.data || {}
      showToast(payload.message || 'Escala gerada.', payload.gaps ? 'warning' : 'success')
      await loadShifts()
    } catch (err) {
      showToast(messageFrom(err, 'Não foi possível gerar a escala'), 'error')
    } finally {
      setBusy(false)
    }
  }

  const saveRoster = async () => {
    if (!patientId) return
    setBusy(true)
    try {
      const response = await dutyRosterService.save({ patientId: Number(patientId), year, month })
      showToast(`Escala salva: ${response.data?.data?.title || 'pronta'}.`, 'success')
      await loadRosters()
    } catch (err) {
      showToast(messageFrom(err, 'Não foi possível salvar a escala'), 'error')
    } finally {
      setBusy(false)
    }
  }

  const openSaved = async (roster) => {
    setBusy(true)
    try {
      const response = await dutyRosterService.load(roster.id)
      const data = response.data?.data || {}
      setPatientId(String(data.patientId || roster.patient_id))
      setYear(Number(data.year || roster.year))
      setMonth(Number(data.month || roster.month))
      setTab('calendar')
      showToast('Escala aberta no calendário.', 'success')
    } catch (err) {
      showToast(messageFrom(err, 'Não foi possível abrir a escala'), 'error')
    } finally {
      setBusy(false)
    }
  }

  const removeRoster = async (roster) => {
    setBusy(true)
    try {
      await dutyRosterService.removeRoster(roster.id)
      showToast('Escala excluída.', 'success')
      await loadRosters()
    } catch (err) {
      showToast(messageFrom(err, 'Não foi possível excluir a escala'), 'error')
    } finally {
      setBusy(false)
    }
  }

  const notifyRoster = async (roster) => {
    setBusy(true)
    try {
      const response = await dutyRosterService.notify(roster.id)
      showToast(response.data?.data?.message || 'Equipe avisada.', 'success')
    } catch (err) {
      showToast(messageFrom(err, 'Não foi possível avisar a equipe'), 'error')
    } finally {
      setBusy(false)
    }
  }

  const acceptConfirm = () => {
    const current = confirm
    setConfirm(null)
    if (current?.kind === 'clear') clearMonth()
    if (current?.kind === 'roster') removeRoster(current.roster)
    if (current?.kind === 'notify') notifyRoster(current.roster)
  }

  const openPicker = (next) => {
    setPickerQuery('')
    setPicker(next)
  }

  const closeMenus = () => {
    setClockOpen(false)
    setScaleOpen(false)
  }

  const sheetModel = () => {
    const schema = currentSchema || user?.organization_schema
    const organization = (organizations || []).find((item) => item.schema_name === schema)
    const count = new Date(year, month, 0).getDate()
    const rows = []
    for (let day = 1; day <= count; day += 1) {
      const key = dateKey(year, month, day)
      const dayShift = shifts.find((item) => item.shift_date === key && item.slot === 'day')
      const nightShift = shifts.find((item) => item.shift_date === key && item.slot === 'night')
      const date = new Date(year, month - 1, day)
      rows.push([
        String(day).padStart(2, '0'),
        WEEKDAYS[date.getDay()],
        dayShift?.user_name || 'Livre',
        dayShift ? `${dayShift.start_time}–${dayShift.end_time}` : hours.day,
        rule === 'diurno' && !nightShift ? '—' : (nightShift?.user_name || 'Livre'),
        nightShift ? `${nightShift.start_time}–${nightShift.end_time}` : (rule === 'diurno' ? '—' : hours.night)
      ])
    }
    return {
      title: `Escala de ${patient?.name || 'paciente'}`,
      subtitle: `${MONTHS[month - 1]} ${year} · ${activeRule.label} · diurno ${hours.day}`,
      clinicName: organization?.label || user?.organization_label || '',
      issuedBy: user?.name || '',
      issuedAt: new Date().toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }),
      columns: ['Dia', 'Semana', 'Diurno', 'Horário', 'Noturno', 'Horário'],
      rows
    }
  }

  const printRoster = () => {
    if (!patientId) {
      showToast('Escolha o paciente para imprimir a escala.', 'warning')
      return
    }
    const opened = printDutySheet(sheetModel())
    if (!opened) showToast('Permita janelas pop-up para imprimir a escala.', 'warning')
  }

  const downloadRoster = async () => {
    if (!patientId) {
      showToast('Escolha o paciente para baixar a escala.', 'warning')
      return
    }
    try {
      await downloadDutySheet(sheetModel(), rosterFileName(patient?.name, MONTHS[month - 1], year))
    } catch {
      showToast('Não foi possível baixar a escala.', 'error')
    }
  }

  return (
    <div className="finance-dashboard patients-module duty-roster-page">
      <Toast message={toast.message} type={toast.type} show={toast.show} onClose={hideToast} />

      <div className="duty-roster-toolbar">
        <div className="duty-roster-tabs">
          <button type="button" className={`duty-roster-tab ${tab === 'calendar' ? 'active' : ''}`} onClick={() => setTab('calendar')}>Calendário</button>
          <button type="button" className={`duty-roster-tab ${tab === 'list' ? 'active' : ''}`} onClick={() => setTab('list')}>Lista de plantões ({shifts.length})</button>
          <button type="button" data-onboarding="roster-saved" className={`duty-roster-tab ${tab === 'saved' ? 'active' : ''}`} onClick={() => setTab('saved')}>Escalas salvas ({rosters.length})</button>
        </div>
        <div className="duty-roster-actions" data-onboarding="roster-generate">
          <button type="button" className="finance-dashboard-period-btn finance-dashboard-period-btn--interactive" onClick={downloadRoster} disabled={!patientId}>
            <CloudDownload size={14} /> Baixar
          </button>
          <button type="button" className="finance-dashboard-period-btn finance-dashboard-period-btn--interactive" onClick={printRoster} disabled={!patientId}>
            <Printer size={14} /> Imprimir
          </button>
          <button type="button" className="duty-roster-tour-help" onClick={() => setTourReplay((current) => current + 1)}>
            <CircleHelp size={14} /> Como usar
          </button>
          {(clockOpen || scaleOpen) && (
            <button type="button" className="duty-roster-pop-backdrop" aria-label="Fechar opções" onClick={closeMenus} />
          )}
          <div className="duty-roster-pop">
            <button
              type="button"
              className="duty-roster-icon-btn"
              title={`Início do diurno: ${dayStart}`}
              aria-label="Ajustar horário de início"
              onClick={() => { setClockOpen((open) => !open); setScaleOpen(false) }}
            >
              <ClockCheck size={16} />
            </button>
            {clockOpen && (
              <div className="duty-roster-menu" role="menu">
                <p>Início do diurno</p>
                {STARTS.map((time) => (
                  <button key={time} type="button" className={time === dayStart ? 'active' : ''} onClick={() => { setDayStart(time); setClockOpen(false) }}>
                    {time}
                    <small>{ranges(time).day} · noite {ranges(time).night}</small>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="duty-roster-pop">
            <button
              type="button"
              className="duty-roster-icon-btn"
              title={activeRule.label}
              aria-label="Opções de escala"
              onClick={() => { setScaleOpen((open) => !open); setClockOpen(false) }}
            >
              <SlidersVertical size={16} />
            </button>
            {scaleOpen && (
              <div className="duty-roster-menu" role="menu">
                <p>Tipo de escala</p>
                {SCALE_RULES.map((item) => (
                  <button key={item.id} type="button" className={item.id === rule ? 'active' : ''} onClick={() => { setRule(item.id); setScaleOpen(false) }}>
                    {item.label}
                    <small>{item.detail}</small>
                  </button>
                ))}
              </div>
            )}
          </div>
          {canEdit && (
            <button type="button" className="duty-roster-tab" onClick={() => setConfirm({ kind: 'clear' })} disabled={busy || !patientId}>
              <Trash2 size={14} /> Limpar
            </button>
          )}
          {canSave && (
            <button type="button" className="duty-roster-tab" onClick={saveRoster} disabled={busy || !patientId}>
              <Save size={14} /> Salvar
            </button>
          )}
          {canGenerate && (
            <button type="button" className="duty-roster-tab active" onClick={generate} disabled={busy || !patientId}>
              <Sparkles size={14} /> Gerar
            </button>
          )}
        </div>
      </div>

      {tab === 'calendar' && (
        <div className="duty-roster-layout">
          <aside className="duty-roster-side">
            <button type="button" className="duty-roster-patient-card" data-onboarding="roster-patient" onClick={() => openPicker({ kind: 'patient' })}>
              <Avatar name={patient?.name || 'Paciente'} email="" imageUrl={patient?.photoUrl} size="md" />
              <div>
                <strong>{patient?.name || 'Escolher paciente'}</strong>
                <small>{patient?.bed || patient?.recordNumber || 'Toque para trocar'}</small>
              </div>
            </button>
            <div className="duty-roster-filter-row">
              <input
                className="duty-roster-search"
                value={peopleQuery}
                onChange={(event) => setPeopleQuery(event.target.value)}
                placeholder="Buscar pessoa"
              />
              <button
                type="button"
                data-onboarding="roster-access"
                className={`duty-roster-icon-btn ${accessOnly ? 'on' : ''}`}
                title="Só quem acessa este paciente"
                aria-pressed={accessOnly}
                onClick={() => setAccessOnly((current) => !current)}
              >
                <Scan size={16} />
              </button>
              <button
                type="button"
                data-onboarding="roster-group"
                className={`duty-roster-icon-btn ${groupId ? 'on' : ''}`}
                title={activeGroup ? activeGroup.name : 'Filtrar por grupo'}
                aria-pressed={Boolean(groupId)}
                onClick={() => openPicker({ kind: 'group' })}
              >
                <ShieldCheck size={16} />
              </button>
            </div>
            {(accessOnly || activeGroup) && (
              <div className={`duty-roster-balloon${accessOnly && activeGroup ? ' is-both' : ''}${!accessOnly && activeGroup ? ' is-group' : ''}`} role="status">
                {accessOnly ? 'Acesso deste paciente' : ''}
                {accessOnly && activeGroup ? ' · ' : ''}
                {activeGroup ? activeGroup.name : ''}
                {accessOnly && !allowedIds ? ' · prontuário aberto para a equipe' : ''}
              </div>
            )}
            <div className="duty-roster-select-all">
              <button
                type="button"
                className="duty-roster-select-all-btn"
                onClick={toggleAllMarked}
                disabled={!visibleIds.length}
                aria-pressed={allVisibleMarked}
                aria-label={allVisibleMarked ? 'Tirar todos da equipe' : 'Marcar todos na equipe'}
              >
                <span>Todos</span>
                <span className={`duty-roster-mark ${allVisibleMarked ? 'on' : ''}`}>
                  {allVisibleMarked ? <CircleCheck size={14} /> : <CircleDashed size={14} />}
                </span>
              </button>
            </div>
            <div className="duty-roster-people" data-onboarding="roster-team">
              {filteredStaff.map((person) => {
                const id = Number(person.id)
                const isMarked = marked.includes(id)
                return (
                  <div
                    key={person.id}
                    className={`duty-roster-person ${isMarked ? 'marked' : ''} ${selectedUserId === id ? 'selected' : ''}`}
                    draggable={canEdit}
                    onDragStart={(event) => {
                      event.dataTransfer.setData('text/plain', String(id))
                      event.dataTransfer.effectAllowed = 'copy'
                    }}
                    onClick={() => canEdit && setSelectedUserId((current) => (current === id ? null : id))}
                  >
                    <span className="duty-roster-avatar-wrap">
                      <Avatar name={person.name} email={person.email || ''} imageUrl={person.avatar_url} size="md" />
                      <button
                        type="button"
                        className={`duty-roster-mark ${isMarked ? 'on' : ''}`}
                        onClick={(event) => {
                          event.stopPropagation()
                          toggleMarked(id)
                        }}
                        aria-label={isMarked ? `Tirar ${person.name} da equipe` : `Marcar ${person.name} na equipe`}
                      >
                        {isMarked ? <CircleCheck size={14} /> : <CircleDashed size={14} />}
                      </button>
                    </span>
                    <div>
                      <p>{person.name}</p>
                      <small>{person.access_group_name || (person.role === 'admin' ? 'Administrador' : 'Equipe')}</small>
                    </div>
                  </div>
                )
              })}
              {!filteredStaff.length && <p className="duty-roster-hint">Ninguém com esse filtro.</p>}
            </div>
            <p className="duty-roster-hint">O círculo marca quem entra na geração. Arraste o avatar para o dia.</p>
          </aside>

          <section className="duty-roster-calendar" data-onboarding="roster-calendar">
            <div className="duty-roster-month">
              <div>
                <strong>{MONTHS[month - 1]} {year}</strong>
                <span>{activeRule.label} · diurno {hours.day}{busy ? ' · carregando' : ''}</span>
              </div>
              <div className="duty-roster-month-nav">
                <button type="button" className="duty-roster-icon-btn" onClick={() => changeMonth(-1)} aria-label="Mês anterior"><ChevronLeft size={16} /></button>
                <button type="button" className="duty-roster-icon-btn" onClick={() => changeMonth(1)} aria-label="Próximo mês"><ChevronRight size={16} /></button>
              </div>
            </div>
            <div className="duty-roster-week">
              {WEEKDAYS.map((day) => <div key={day}>{day}</div>)}
            </div>
            <div className="duty-roster-grid">
              {grid.map((day, index) => {
                if (!day) return <div key={`empty-${index}`} className="duty-roster-day outside" />
                const lines = [
                  ['day', 'Dia', shiftOn(day, 'day')],
                  ['night', 'Noite', shiftOn(day, 'night')]
                ].filter(([slot, , shift]) => rule !== 'diurno' || slot === 'day' || shift)
                return (
                  <div key={day} className="duty-roster-day">
                    <button type="button" className="duty-roster-day-number" onClick={() => setOpenDay(day)}>{day}</button>
                    {lines.map(([slot, label, shift]) => (
                      <button
                        key={slot}
                        type="button"
                        className={`duty-roster-line ${slot} ${shift ? 'filled' : ''}`}
                        onDragOver={(event) => canEdit && !shift && event.preventDefault()}
                        onDrop={(event) => !shift && onDrop(event, day, slot)}
                        onClick={() => {
                          if (shift) setOpenDay(day)
                          else if (selectedUserId) place(day, slot)
                          else setOpenDay(day)
                        }}
                      >
                        {shift ? (
                          <>
                            <Avatar name={shift.user_name || ''} email="" imageUrl={shift.avatar_url} size="sm" />
                            <span>{firstName(shift.user_name)}</span>
                          </>
                        ) : (
                          <span>{label}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )
              })}
            </div>
          </section>
        </div>
      )}

      {tab === 'list' && (
        <section className="duty-roster-panel">
          <table className="duty-roster-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Turno</th>
                <th>Horário</th>
                <th>Pessoa</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shifts.map((shift) => (
                <tr key={shift.id}>
                  <td>{String(shift.shift_date).split('-').reverse().join('/')}</td>
                  <td>{shift.slot === 'night' ? 'Noturno' : 'Diurno'}</td>
                  <td>{shift.start_time}–{shift.end_time}</td>
                  <td>{shift.user_name}</td>
                  <td>
                    {canEdit && (
                      <button type="button" className="duty-roster-icon-btn" aria-label="Tirar plantão" onClick={() => removeShift(shift.id)}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!shifts.length && (
                <tr><td colSpan={5}>Nenhum plantão neste mês.</td></tr>
              )}
            </tbody>
          </table>
        </section>
      )}

      {tab === 'saved' && (
        <section className="duty-roster-panel">
          <div className="duty-roster-cards">
            {rosters.map((roster) => (
              <article key={roster.id} className="duty-roster-card">
                <h3>{roster.title}</h3>
                <p>{roster.patient_name}</p>
                <p>{roster.total_shifts} plantões · {roster.total_hours} h · {roster.caregivers_count} na equipe</p>
                <small>{roster.saved_by_name || 'Equipe'}</small>
                <div className="duty-roster-card-actions">
                  <button type="button" className="finance-dashboard-new-btn" onClick={() => openSaved(roster)} disabled={busy}>Abrir no calendário</button>
                  {canSave && (
                    <button type="button" className="duty-roster-tab" onClick={() => setConfirm({ kind: 'notify', roster })} disabled={busy}>
                      <Bell size={14} /> Notificar
                    </button>
                  )}
                  {canSave && (
                    <button type="button" className="duty-roster-icon-btn" aria-label="Excluir escala" onClick={() => setConfirm({ kind: 'roster', roster })}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </article>
            ))}
            {!rosters.length && <p className="duty-roster-hint">Nenhuma escala salva ainda.</p>}
          </div>
        </section>
      )}

      {openDay && (
        <div className="duty-roster-modal" onClick={() => setOpenDay(null)}>
          <div className="duty-roster-dialog" onClick={(event) => event.stopPropagation()}>
            <h3>{openDay} de {MONTHS[month - 1]}</h3>
            {[['day', 'Diurno', hours.day], ['night', 'Noturno', hours.night]]
              .filter(([slot]) => rule !== 'diurno' || slot === 'day' || shiftOn(openDay, slot))
              .map(([slot, label, range]) => {
                const shift = shiftOn(openDay, slot)
                return (
                  <div key={slot} className="duty-roster-shift-row">
                    <div>
                      <strong>{label}</strong>
                      <span>{range} · {shift?.user_name || 'Livre'}</span>
                    </div>
                    <div className="duty-roster-shift-actions">
                      {canEdit && (
                        <button
                          type="button"
                          className="duty-roster-tab"
                          onClick={() => openPicker({ kind: 'person', shiftId: shift?.id || null, day: openDay, slot })}
                        >
                          {shift ? 'Trocar' : 'Escolher'}
                        </button>
                      )}
                      {canEdit && shift && (
                        <button type="button" className="duty-roster-icon-btn" aria-label="Tirar plantão" onClick={() => removeShift(shift.id)}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            <button type="button" className="duty-roster-tab" onClick={() => setOpenDay(null)}>Fechar</button>
          </div>
        </div>
      )}

      <ERPSelectSearchModal
        isOpen={Boolean(picker)}
        onClose={() => setPicker(null)}
        title={picker?.kind === 'patient' ? 'Paciente' : picker?.kind === 'group' ? 'Grupo de acesso' : 'Pessoa do plantão'}
        searchPlaceholder={picker?.kind === 'patient' ? 'Buscar paciente' : picker?.kind === 'group' ? 'Buscar grupo' : 'Buscar pessoa'}
        searchTerm={pickerQuery}
        onSearchChange={setPickerQuery}
      >
        {picker?.kind === 'group' && (
          <ERPSelectModalItem
            name="Todos os grupos"
            detail="Tira o filtro de grupo"
            selected={!groupId}
            checkIcon={<Check size={16} />}
            onClick={() => { setGroupId(null); setPicker(null) }}
          />
        )}
        {(picker?.kind === 'group' ? groups : pickerItems)
          .filter((item) => {
            if (picker?.kind !== 'group') return true
            const query = pickerQuery.trim().toLowerCase()
            return !query || String(item.name || '').toLowerCase().includes(query)
          })
          .map((item) => (
            <ERPSelectModalItem
              key={item.id}
              name={item.name}
              detail={picker?.kind === 'patient' ? (item.bed || item.recordNumber || '') : (item.access_group_name || '')}
              selected={picker?.kind === 'group' && Number(groupId) === Number(item.id)}
              avatar={picker?.kind === 'group' ? null : (
                <Avatar
                  name={item.name}
                  email={item.email || ''}
                  imageUrl={picker?.kind === 'patient' ? item.photoUrl : item.avatar_url}
                  size="sm"
                />
              )}
              checkIcon={<Check size={16} />}
              onClick={() => {
                if (picker?.kind === 'patient') {
                  setPatientId(String(item.id))
                  setPicker(null)
                  return
                }
                if (picker?.kind === 'group') {
                  setGroupId(item.id)
                  setPicker(null)
                  return
                }
                if (picker?.shiftId) changeShiftUser(picker.shiftId, item.id)
                else place(picker.day, picker.slot, item.id).then(() => setPicker(null))
              }}
            />
          ))}
        {picker?.kind !== 'group' && !pickerItems.length && (
          <ERPSelectModalEmpty message="Nada encontrado" />
        )}
      </ERPSelectSearchModal>

      <ConfirmModal
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={acceptConfirm}
        title={confirm?.kind === 'roster' ? 'Excluir escala' : confirm?.kind === 'notify' ? 'Avisar a equipe' : 'Limpar mês'}
        type="warning"
        confirmText={confirm?.kind === 'roster' ? 'Excluir' : confirm?.kind === 'notify' ? 'Notificar' : 'Limpar'}
        message={confirm?.kind === 'roster'
          ? 'Excluir esta escala salva? O calendário atual continua.'
          : confirm?.kind === 'notify'
            ? 'Cada pessoa da escala recebe um aviso no sino, com os dias e horários dela. O aviso abre Meus plantões.'
            : 'Limpar todos os plantões deste paciente neste mês?'}
      />

      <OnboardingTour
        tourId={DUTY_ROSTER_TOUR_ID}
        steps={DUTY_ROSTER_ONBOARDING_STEPS}
        userId={user?.id}
        ready={ready}
        replayToken={tourReplay}
      />
    </div>
  )
}
